// Automatic CAD layer classification for Taman.
// Maps a layer name (+ optional hatch pattern / ACI colour) to a Taman polygon
// type and a role. Dictionaries: EN, HR, DE, IT + US NCS/AIA, ISO 13567 / BS 1192
// descriptive field, Swiss GEOBAU (SN 612 020) codes, Vienna MZK codes (decoder hook).

type TamanType = 'Road' | 'Sidewalk' | 'Building' | 'Environment' | 'Unspecified';
/** surface: closed areas; edge: boundary lines (curbs, road edges) useful for polygonizing;
 *  lighting: existing luminaires (INSERT positions); ignore: annotation, utilities, cadastre */
type LayerRole = 'surface' | 'edge' | 'lighting' | 'ignore';

export interface LayerClass {
  type: TamanType;
  role: LayerRole;
  confidence: number; // 0..1
  matched: string[];
}

interface Rule {
  type: TamanType | null;
  role: LayerRole;
  w: number;
  /** Terms are matched against normalised tokens: exact token match for short
   *  terms (< 5 chars) and substring match for longer ones (German compounds). */
  terms: string[];
}

// Normalisation: lower case, strip diacritics (č ć š ž đ ä ö ü à è ...), ß→ss, đ→dj.
function normalise(s: string): string {
  return s
    .replace(/ß/g, 'ss')
    .replace(/[Đđ]/g, 'dj')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

// Umlaut transliteration variants (Gruenflaeche -> grunflache) handled by also
// matching the "ae/oe/ue -> a/o/u" folded form.
function fold(s: string): string {
  return s.replace(/ae/g, 'a').replace(/oe/g, 'o').replace(/ue/g, 'u');
}

const RULES: Rule[] = [
  // ── Lighting (existing luminaires, poles) ──
  {
    type: null,
    role: 'lighting',
    w: 3,
    terms: [
      'lite',
      'light',
      'lights',
      'lighting',
      'streetlight',
      'streetlights',
      'luminaire',
      'luminaires',
      'lamp',
      'lamps',
      'lamppost',
      'beleuchtung',
      'strassenbeleuchtung',
      'leuchte',
      'leuchten',
      'lampe',
      'laterne',
      'lichtmast',
      'rasvjeta',
      'javna rasvjeta',
      'svjetiljka',
      'svjetiljke',
      'rasvjetni',
      'lampa',
      'jr',
      'illuminazione',
      'lampione',
      'lampioni',
      'punto luce',
      'punti luce',
      'pubblica illuminazione',
      'ip',
    ],
  },
  // ── Roads ──
  {
    type: 'Road',
    role: 'surface',
    w: 3,
    terms: [
      'road',
      'roads',
      'roadway',
      'carriageway',
      'street',
      'streets',
      'highway',
      'hwy',
      'motorway',
      'rd',
      'rds',
      'asphalt',
      'asph',
      'pvmt',
      'paving-road',
      'parking',
      'pkng',
      'carpark',
      'driveway',
      'drive',
      'lane',
      'lanes',
      'traffic',
      'roundabout',
      'intersection',
      'junction',
      'busway',
      'bus',
      'tram',
      'strasse',
      'strassen',
      'fahrbahn',
      'fahrbahnen',
      'verkehrsflache',
      'verkehrsflachen',
      'fahrspur',
      'parkplatz',
      'parkplatze',
      'stellplatz',
      'stellplatze',
      'parkflache',
      'zufahrt',
      'kreisverkehr',
      'kreuzung',
      'autobahn',
      'fahrweg',
      'asphaltflache',
      'strassenflache',
      'befestigte flache',
      '~strass',
      '~fahrbahn',
      'cesta',
      'ceste',
      'kolnik',
      'kolnici',
      'kolnicka',
      'kolni',
      'prometnica',
      'prometnice',
      'promet',
      'ulica',
      'ulice',
      'parkiraliste',
      'parkiralista',
      'asfalt',
      'raskrizje',
      'kruzni tok',
      'autocesta',
      'prilaz',
      'kolni prilaz',
      'strada',
      'strade',
      'stradale',
      'stradali',
      'carreggiata',
      'carreggiate',
      'sede stradale',
      'viabilita',
      'asfalto',
      'parcheggio',
      'parcheggi',
      'rotatoria',
      'incrocio',
      'autostrada',
      'corsia',
      'aree stradali',
    ],
  },
  // ── Sidewalks / pedestrian / cycle ──
  {
    type: 'Sidewalk',
    role: 'surface',
    w: 3,
    terms: [
      'sidewalk',
      'sidewalks',
      'footway',
      'footpath',
      'walkway',
      'walk',
      'walks',
      'swlk',
      'sdwk',
      'pedestrian',
      'ped',
      'plaza',
      'square',
      'promenade',
      'cycleway',
      'bikeway',
      'bike',
      'bicycle',
      'cycle',
      'trail',
      'crosswalk',
      'hardscape',
      'hrdscp',
      'hard',
      'paving',
      'steps',
      'stairs',
      'gehweg',
      'gehwege',
      'burgersteig',
      'fussweg',
      'fusswege',
      'fussganger',
      'fussgangerzone',
      'gehbahn',
      'radweg',
      'radwege',
      'trottoir',
      'platz',
      'pflaster',
      'gehwegplatten',
      'treppe',
      'treppen',
      'weg',
      'wege',
      'schmaler weg',
      '~gehweg',
      '~fussweg',
      '~radweg',
      'stufen',
      'zebrastreifen',
      'nogostup',
      'nogostupi',
      'plocnik',
      'plocnici',
      'pjesacka',
      'pjesacki',
      'pjesacke',
      'setnica',
      'setnice',
      'biciklisticka',
      'biciklisticke',
      'trg',
      'trgovi',
      'staza',
      'staze',
      'stepenice',
      'pothodnik',
      'marciapiede',
      'marciapiedi',
      'pedonale',
      'pedonali',
      'ciclabile',
      'ciclabili',
      'ciclopedonale',
      'piazza',
      'piazze',
      'sentiero',
      'sentieri',
      'gradinata',
      'scalinata',
      'percorso pedonale',
    ],
  },
  // ── Buildings ──
  {
    type: 'Building',
    role: 'surface',
    w: 3,
    terms: [
      'building',
      'buildings',
      'build',
      'bldg',
      'bldgs',
      'bld',
      'house',
      'houses',
      'structure',
      'structures',
      'footprint',
      'footprints',
      'roof',
      'roofs',
      'wall',
      'walls',
      'facade',
      'garage',
      'shed',
      'gebaude',
      'gebauden',
      'haus',
      'hauser',
      'baukorper',
      'hauptgebaude',
      'nebengebaude',
      'bauteil',
      'bauteile',
      'dach',
      'hauslinie',
      'uberbauung',
      'glashaus',
      'flugdach',
      'schuppen',
      '~gebaud',
      '~haus',
      'zgrada',
      'zgrade',
      // Croatian cadastral plans (DKP): 2_zg is the building layer.
      'zg',
      'objekt',
      'objekti',
      'gradjevina',
      'gradjevine',
      'kuca',
      'kuce',
      'krov',
      'krovovi',
      'zgr',
      'garaza',
      'tlocrt',
      'edificio',
      'edifici',
      'fabbricato',
      'fabbricati',
      'costruzione',
      'costruzioni',
      'casa',
      'case',
      'tettoia',
      'tettoie',
      'baracca',
      'sagoma',
    ],
  },
  // ── Green / environment ──
  {
    type: 'Environment',
    role: 'surface',
    w: 3,
    terms: [
      'green',
      'greenery',
      'grass',
      'lawn',
      'landscape',
      'landscaping',
      'planting',
      'plant',
      'plants',
      'plnt',
      'tree',
      'trees',
      'shrub',
      'shrubs',
      'hedge',
      'hedges',
      'park',
      'garden',
      'vegetation',
      'veg',
      'forest',
      'wood',
      'woods',
      'turf',
      'meadow',
      'planter',
      'verge',
      'water',
      'pond',
      'lake',
      'river',
      'grun',
      'grunflache',
      'grunflachen',
      'rasen',
      'wiese',
      'baum',
      'baume',
      'geholz',
      'hecke',
      'hecken',
      'strauch',
      'straucher',
      'gebusch',
      'bepflanzung',
      'pflanzung',
      'wald',
      'waldflache',
      'waldflachen',
      'garten',
      'gartenanlage',
      'beet',
      'grunstreifen',
      'strassenbegleitgrun',
      'gewasser',
      'humusierte',
      '~baum',
      '~grun',
      '~rasen',
      '~wiese',
      'zelenilo',
      'zelena',
      'zelene',
      'zeleni',
      'travnjak',
      'travnjaci',
      'trava',
      'drvo',
      'drvece',
      'stablo',
      'stabla',
      'grmlje',
      'grm',
      'zivica',
      'perivoj',
      'vrt',
      'vrtovi',
      'sadnja',
      'hortikultura',
      'suma',
      'vegetacija',
      'cvjetnjak',
      'verde',
      'verdi',
      'aiuola',
      'aiuole',
      'prato',
      'prati',
      'albero',
      'alberi',
      'alberature',
      'siepe',
      'siepi',
      'filari',
      'cespugli',
      'arbusti',
      'parco',
      'giardino',
      'giardini',
      'vegetazione',
      'bosco',
      'boscata',
      'area verde',
      'filarisiepi',
    ],
  },
  // ── Edges: curbs, road edges, centre lines (boundaries, feed the polygonizer) ──
  {
    type: 'Road',
    role: 'edge',
    w: 2.5,
    terms: [
      'curb',
      'curbs',
      'kerb',
      'kerbs',
      'gutter',
      'eop',
      'edge of pavement',
      'centerline',
      'centreline',
      'cntr',
      'clin',
      'bordstein',
      'bordsteine',
      'bordsteinkante',
      'rinne',
      'fahrbahnrand',
      'strassenbegrenzung',
      'belagsbegrenzung',
      'strassenachse',
      'rubnjak',
      'rubnjaci',
      'os ceste',
      'rub kolnika',
      'cordolo',
      'cordoli',
      'ciglio',
      'mezzeria',
    ],
  },
  { type: 'Sidewalk', role: 'edge', w: 2.5, terms: ['gehsteigkante', 'gehsteig', 'rub nogostupa'] },
  // ── Ignore: annotation, dimensions, sheet, survey, cadastre, contours, utilities ──
  {
    type: null,
    role: 'ignore',
    w: 2,
    terms: [
      'text',
      'txt',
      'texts',
      'anno',
      'annotation',
      'label',
      'labels',
      'note',
      'notes',
      'dim',
      'dims',
      'dimension',
      'dimensions',
      'title',
      'ttlb',
      'titleblock',
      'border',
      'frame',
      'sheet',
      'legend',
      'north',
      'grid',
      'defpoints',
      'viewport',
      'vport',
      'xref',
      'logo',
      'symb',
      'iden',
      'patt',
      'nplt',
      'hatchpattern',
      'revision',
      'beschriftung',
      'bemassung',
      'masse',
      'kote',
      'koten',
      'hausnummer',
      'nummer',
      'strassennamen',
      'namen',
      'eigennamen',
      'rahmen',
      'legende',
      'nordpfeil',
      'raster',
      'opis',
      'natpis',
      'natpisi',
      'tekst',
      'kota',
      'kucni broj',
      'nazivi',
      'okvir',
      'legenda',
      'sjever',
      'mreza',
      'testo',
      'testi',
      'scritte',
      'quota',
      'quote',
      'civici',
      'toponimi',
      'toponomastica',
      'cartiglio',
      'griglia',
      // survey points & terrain
      'point',
      'points',
      'spot',
      'spots',
      'elev',
      'elevation',
      'contour',
      'contours',
      'cont',
      'topo',
      'hohe',
      'hohen',
      'hohenlinie',
      'hohenlinien',
      'gelandehohen',
      'hoehenpunkt',
      'tocke',
      'tocka',
      'visina',
      'visine',
      'izohipse',
      'slojnice',
      'punti',
      'punto',
      'pquotati',
      'quotati',
      'curve di livello',
      'curvliv',
      'curvliv2',
      'curvliv10',
      'festpunkt',
      'festpunkte',
      // cadastre / property
      'prop',
      'property',
      'parcel',
      'parcels',
      'lot',
      'lots',
      'easement',
      'setback',
      'row',
      'flurstuck',
      'grenze',
      'grenzen',
      'kataster',
      'grundstuck',
      'katastar',
      'cestica',
      'cestice',
      'medja',
      'particella',
      'catasto',
      'confine',
      'grenzpunkt',
      'bezirksgrenze',
      // utilities
      'util',
      'utility',
      'utilities',
      'sewer',
      'storm',
      'strm',
      'sanitary',
      'ssan',
      'sswr',
      'watr',
      'gas',
      'elec',
      'powr',
      'tele',
      'comm',
      'fiber',
      'irrg',
      'irrigation',
      'abwasser',
      'abwasserentsorgung',
      'wasserversorgung',
      'gasversorgung',
      'stromversorgung',
      'fernmelde',
      'fernmeldeanlagen',
      'fernwarme',
      'kanal',
      'entsorgung',
      'versorgung',
      'einbauten',
      'schacht',
      'kanalizacija',
      'vodovod',
      'plin',
      'plinovod',
      'elektro',
      'telekomunikacije',
      'odvodnja',
      'dtk',
      'fognatura',
      'acquedotto',
      'gasdotto',
      'reti',
      'enel',
      'canalette',
      // fences, railings, misc linework
      'fence',
      'fences',
      'railing',
      'zaun',
      'zaune',
      'gelander',
      'einfriedung',
      'einfriedungen',
      'ograda',
      'ograde',
      'recinzione',
      'recinzioni',
      'murirecinz',
      'unsichtbare',
      'simboli',
      'stadtmobel',
      'furniture',
      'furn',
      'adresse',
      'address',
      'rail',
      'rails',
      'railway',
      'gleis',
      'gleise',
      'schiene',
      'schienen',
      'ferrovia',
      'ferrovie',
      'pruga',
      'tracks',
    ],
  },
  // weak mixed paving layer (road + sidewalk surfaces), e.g. "Befestigungsarten"
  {
    type: 'Road',
    role: 'surface',
    w: 1.5,
    terms: [
      'befestigung',
      'befestigungsarten',
      'befestigte',
      'paved',
      'pavement',
      'pavimentazione',
      'kolnicka konstrukcija',
    ],
  },
  { type: null, role: 'lighting', w: 1.5, terms: ['mast', 'stup', 'stupovi', 'palo', 'pali'] },
];

/** US NCS / AIA layer name: <discipline>-<major>[-<minor>][-<status>] */
const NCS_MAJOR: Record<string, [TamanType | null, LayerRole]> = {
  ROAD: ['Road', 'surface'],
  PKNG: ['Road', 'surface'],
  PVMT: ['Road', 'surface'],
  CURB: ['Road', 'edge'],
  WALK: ['Sidewalk', 'surface'],
  SWLK: ['Sidewalk', 'surface'],
  HARD: ['Sidewalk', 'surface'],
  BLDG: ['Building', 'surface'],
  WALL: ['Building', 'surface'],
  ROOF: ['Building', 'surface'],
  PLNT: ['Environment', 'surface'],
  TREE: ['Environment', 'surface'],
  LAWN: ['Environment', 'surface'],
  LITE: [null, 'lighting'],
  ANNO: [null, 'ignore'],
  DIMS: [null, 'ignore'],
  TEXT: [null, 'ignore'],
  SYMB: [null, 'ignore'],
  TTLB: [null, 'ignore'],
  GRID: [null, 'ignore'],
  NPLT: [null, 'ignore'],
  PROP: [null, 'ignore'],
  TOPO: [null, 'ignore'],
  STRM: [null, 'ignore'],
  SSWR: [null, 'ignore'],
  WATR: [null, 'ignore'],
  GAS: [null, 'ignore'],
  POWR: [null, 'ignore'],
  COMM: [null, 'ignore'],
  UTIL: [null, 'ignore'],
  IRRG: [null, 'ignore'],
  GRAD: [null, 'ignore'],
  ESMT: [null, 'ignore'],
};
const NCS_DISCIPLINE_PRIOR: Record<string, [TamanType | null, LayerRole]> = {
  L: ['Environment', 'surface'],
  A: ['Building', 'surface'],
  G: [null, 'ignore'],
};

/** Swiss GEOBAU DXF (SN 612 020) numeric layer codes. */
const GEOBAU: Record<string, [TamanType | null, LayerRole]> = {
  '01211': ['Building', 'surface'],
  '01211p': ['Building', 'surface'],
  '01221': ['Road', 'surface'],
  '01222': ['Unspecified', 'surface'],
  '01225': ['Sidewalk', 'surface'],
  '01231': ['Environment', 'surface'],
  '01232': ['Environment', 'surface'],
  '01233': ['Environment', 'surface'],
  '01234': ['Environment', 'surface'],
  '01235': ['Environment', 'surface'],
  '01236': ['Environment', 'surface'],
  '01241': ['Environment', 'surface'],
  '01251': ['Environment', 'surface'],
  '01252': ['Environment', 'surface'],
  '01312': ['Sidewalk', 'surface'],
  '01331': ['Sidewalk', 'edge'],
  '01332': ['Sidewalk', 'surface'],
  '01362': ['Environment', 'surface'],
};

/** Optional decoder for coded layer schemas (e.g. Vienna MZK "2M_TSGS_L" -> "Gehsteigkante"). */
type SchemaDecoder = (layer: string) => string | null;

/** Suffix/modifier tokens that turn any feature layer into an annotation layer (C-ROAD-TEXT, ctc_edifici_altezza). */
const ANNOTATION_MODIFIERS = new Set([
  'text',
  'txt',
  'anno',
  'label',
  'labels',
  'dims',
  'dim',
  'name',
  'names',
  'namen',
  'nummer',
  'nr',
  'num',
  'no',
  'altezza',
  'altezze',
  'height',
  'heights',
  'hohe',
  'visina',
  'quota',
  'quote',
  'testo',
  'beschriftung',
  'oznaka',
  'oznake',
  'broj',
  'kota',
  'kote',
  'notes',
  'note',
  'tag',
  'tags',
  'iden',
]);

function tokens(nameNorm: string): string[] {
  return nameNorm.split(/[^a-z0-9]+|(?<=[a-z])(?=[0-9])|(?<=[0-9])(?=[a-z])/).filter(Boolean);
}

export function classifyLayer(
  layer: string,
  hints: { hatchPatterns?: string[]; decoder?: SchemaDecoder } = {},
): LayerClass {
  const out = (type: TamanType, role: LayerRole, confidence: number, matched: string[]): LayerClass => ({
    type,
    role,
    confidence,
    matched,
  });
  // 1. schema tables
  const geobau = /^0?1[2-8]\d\dp?$/.test(layer.trim()) ? GEOBAU[layer.trim().padStart(5, '0')] : undefined;
  if (geobau) return out(geobau[0] ?? 'Unspecified', geobau[1], 0.95, ['GEOBAU ' + layer]);
  let text = layer;
  const decoded = hints.decoder?.(layer);
  if (decoded) text = decoded + ' ' + layer;

  const norm = normalise(text.replace(/([a-z])([A-Z])/g, '$1 $2'));
  const variants = [norm, fold(norm)];
  // each token is matched in its original and ae/oe/ue-folded form (Gruenflaeche -> grunflache)
  const tokList = tokens(variants[0]);
  const toks = new Set<string>(tokList);
  const forms = tokList.map((tk) => (fold(tk) === tk ? [tk] : [tk, fold(tk)]));
  const joined = variants.map((v) => ' ' + tokens(v).join(' ') + ' ');

  const score = new Map<string, { type: TamanType | null; role: LayerRole; s: number; m: string[] }>();
  const add = (type: TamanType | null, role: LayerRole, w: number, term: string) => {
    const k = (type ?? '-') + '/' + role;
    const cur = score.get(k) ?? { type, role, s: 0, m: [] };
    cur.s += w;
    cur.m.push(term);
    score.set(k, cur);
  };

  // 2. NCS / AIA pattern  e.g. C-ROAD-CURB, L-PLNT, A-WALL, E-LITE-EXTR
  const ncs = /^([A-Z])([A-Z])?-([A-Z0-9]{2,4})(?:-([A-Z0-9]{2,4}))?(?:-([A-Z0-9]{1,4}))?$/i.exec(layer.trim());
  if (ncs) {
    const major = ncs[3].toUpperCase(),
      minor = (ncs[4] ?? '').toUpperCase();
    const MINOR_OVERRIDES = new Set([
      'CURB',
      'WALK',
      'SWLK',
      'PKNG',
      'PVMT',
      'TREE',
      'PLNT',
      'LITE',
      'TEXT',
      'ANNO',
      'DIMS',
      'SYMB',
      'NPLT',
      'TTLB',
    ]);
    const useMinor = MINOR_OVERRIDES.has(minor) && NCS_MAJOR[minor];
    const mm = useMinor ? NCS_MAJOR[minor] : NCS_MAJOR[major];
    if (mm) add(mm[0], mm[1], 4, 'NCS ' + (useMinor ? minor : major));
    if (/^(CNTR|CENT|CL|EDGE|EOP)$/.test(minor) && major === 'ROAD') add('Road', 'edge', 4.5, 'NCS ' + minor);
    const prior = NCS_DISCIPLINE_PRIOR[ncs[1].toUpperCase()];
    if (prior) add(prior[0], prior[1], 0.75, 'NCS discipline ' + ncs[1].toUpperCase());
  }

  // 3. dictionary: per rule, each token contributes its single best-matching term
  //    (no stacking of synonyms); German compounds are head-final, so a suffix
  //    match ("Strassenbegleitgrun" -> green, "Parkplatz" -> car park) beats an infix.
  for (const r of RULES) {
    const hits: string[] = [];
    let total = 0;
    for (const pair of forms) {
      let best = 0,
        bestTerm = '';
      for (const tk of pair)
        for (const term of r.terms) {
          if (term.includes(' ')) continue;
          let w = 0;
          if (term.startsWith('~')) {
            const root = term.slice(1);
            if (tk.includes(root)) w = tk.endsWith(root) || tk.startsWith(root) ? r.w * 0.8 : r.w * 0.5;
          } else if (tk === term) w = r.w + 0.25;
          else if (term.length >= 5 && tk.includes(term)) w = tk.endsWith(term) ? r.w : r.w * 0.6;
          if (w > best) {
            best = w;
            bestTerm = term;
          }
        }
      if (best) {
        total += best;
        hits.push(bestTerm);
      }
    }
    for (const term of r.terms) {
      if (term.includes(' ') && joined.some((j) => j.includes(' ' + term + ' '))) {
        total += r.w + 0.5;
        hits.push(term);
      }
    }
    if (total) add(r.type, r.role, total, [...new Set(hits)].join('|'));
  }
  // 4. annotation modifiers win over the feature noun
  const longMods = [...ANNOTATION_MODIFIERS].filter((m) => m.length >= 5);
  const mods = [...toks].filter(
    (tk) => ANNOTATION_MODIFIERS.has(tk) || longMods.some((m) => tk.length > m.length && tk.endsWith(m)),
  );
  if (mods.length) add(null, 'ignore', 6, 'modifier ' + mods.join(','));

  // 5. hatch pattern hints
  for (const p of hints.hatchPatterns ?? []) {
    const P = p.toUpperCase();
    if (/GRASS|GRAVEL|TREE|EARTH/.test(P)) add('Environment', 'surface', 1, 'hatch ' + P);
    else if (/AR-CONC|CONC|AR-B816|AR-HBONE|AR-PARQ|BRICK|BRSTONE|SQUARE/.test(P))
      add('Sidewalk', 'surface', 0.75, 'hatch ' + P);
    else if (/AR-SAND|ASPH/.test(P)) add('Road', 'surface', 0.75, 'hatch ' + P);
  }

  if (!score.size) return out('Unspecified', 'surface', 0, []);
  const ranked = [...score.values()].sort((a, b) => b.s - a.s);
  const best = ranked[0];
  const second = ranked[1]?.s ?? 0;
  const confidence = Math.max(0, Math.min(1, (best.s - second) / 4 + (best.s >= 3 ? 0.4 : 0.2)));
  return out(best.type ?? 'Unspecified', best.role, Number(confidence.toFixed(2)), best.m);
}
